import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';
import type { Product } from '@/contexts/ProductContext';

export function useProducts() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery({
    queryKey: ['products', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Product[];
    },
    enabled: !!tenant?.id,
  });

  const createProduct = useMutation({
    mutationFn: async (product: {
      name: string;
      category: 'physical' | 'digital' | 'service' | 'subscription';
      product_type: 'ebook' | 'course' | 'saas';
      target_audience: 'b2c' | 'b2b' | 'b2b2c';
      logo_url?: string | null;
      data_source?: 'manual' | 'integration' | 'upload';
      gateway_product_id?: string | null;
    }) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('products')
        .insert({
          ...product,
          tenant_id: tenant.id,
          created_by: user?.id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produto criado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao criar produto: ' + error.message);
    },
  });

  const updateProduct = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Product> }) => {
      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produto atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar produto: ' + error.message);
    },
  });

  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produto removido com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao remover produto: ' + error.message);
    },
  });

  const uploadLogo = async (file: File, tenantId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${tenantId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('product-logos')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('product-logos')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  return {
    products,
    isLoading,
    createProduct,
    updateProduct,
    deleteProduct,
    uploadLogo,
  };
}
